import { BottomSheetContainer } from './BottomSheetContainer';
import { screensService } from '../../../../../../api';
import {
  Flex,
  List,
  Spinner,
  useScreenSize,
  createUseBottomSheetModal,
  type ListRef,
} from '@banhala/ui-kit';
import { FilterType } from '../../../../../../api/screensService/getScreensFilterOptions/enums';
import {
  useFilterListBottomSheetAtom,
  type SetFilterListBottomSheetViaQueryValueProps,
} from '../../Provider/filterListBottomSheet/useFilterListBottomSheetAtom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { MultiImageFilter } from '../MultiImageFilter/MultiImageFilter';
import { MultiFlowFilter } from '../MultiFlowFilter/MultiFlowFilter';
import { Footer } from './Footer';
import { useState, useRef, useMemo } from 'react';
import { useListHeight } from '../../hooks/useListHeight';
import { SingleFilter } from '../SingleFilter';
import { SingleTreeFilter } from '../SingleTreeFilter';
import { SlideFilter } from '../SlideFilter';
import { delay, useAsyncEffect } from '../../../../../../utils';
import { useLogViewFilterBottomSheet } from './useLogViewFilterBotomShet';
import { useScrollFilterWhenClickChip } from './useScrollFilterWhenClickChip';
import {
  FilterSheetOverrideProvider,
  type FilterSheetOverride,
} from '../../Provider/FilterSheetOverrideContext';
import { InstanceIdProvider } from '../../../../../../hooks';
import { FilterScopeProvider } from '../../Provider/filterList/FilterScopeContext';
import type { FormattedChildGroup } from '../../../../../../api/screensService/getScreensFilterOptions/types';

export const useFilterBottomSheet = createUseBottomSheetModal<
  void,
  {
    filterToken: string;
    scrollToFilterList: VoidFunction;
    radioQueryName?: string;
    /**
     * 호출부의 필터 스코프(FilterScopeProvider 값). 시트는 ModalProvider 아래(호출부 트리 밖)
     * 에서 렌더되어 컨텍스트가 닿지 않으므로 명시적으로 넘겨받아 다시 주입한다.
     * 미지정이면 instanceId 스코프(기존 동작).
     */
    filterScope?: string | null;
    /**
     * 별도 RN surface(modal)로 시트가 띄워질 때, 호출 screen의 instanceId/navigation/route를
     * 시트 내부에 주입하기 위한 override. 인라인 사용 시에는 undefined.
     */
    override?: FilterSheetOverride & { originalInstanceId: string };
  }
>(
  ({
    cancel,
    param: {
      filterToken,
      scrollToFilterList,
      radioQueryName,
      filterScope,
      override,
    },
  }) => {
    const inner = (
      <BottomSheetContainer onPressClose={cancel}>
        <ButtonSheetContent
          filterToken={filterToken}
          closeBottomSheet={cancel}
          scrollToFilterList={scrollToFilterList}
          radioQueryName={radioQueryName}
        />
      </BottomSheetContainer>
    );

    // 호출부 스코프를 복원한다(없으면 instanceId 스코프 유지).
    const content =
      filterScope == null ? (
        inner
      ) : (
        <FilterScopeProvider scope={filterScope}>{inner}</FilterScopeProvider>
      );

    if (!override) return content;

    return (
      <InstanceIdProvider value={override.originalInstanceId}>
        <FilterSheetOverrideProvider
          value={{
            setParams: override.setParams,
            routeParams: override.routeParams,
          }}
        >
          {content}
        </FilterSheetOverrideProvider>
      </InstanceIdProvider>
    );
  },
);

const ButtonSheetContent = ({
  filterToken,
  closeBottomSheet,
  scrollToFilterList,
  radioQueryName,
}: {
  filterToken: string;
  closeBottomSheet: () => void;
  scrollToFilterList: VoidFunction;
  radioQueryName?: string;
}) => {
  const {
    filterListBottomSheetAtomValue,
    setFilterListBottomSheetViaQueryValue,
  } = useFilterListBottomSheetAtom();

  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const { height } = useScreenSize();
  const { data, isFetching, isPlaceholderData } = useQuery({
    queryKey: [
      'atomicFilterOptionsDrawer',
      filterToken,
      filterListBottomSheetAtomValue,
    ],
    queryFn: () => {
      return screensService.getFilterOptions({
        filter_token: filterToken,
        ...filterListBottomSheetAtomValue,
      });
    },
    placeholderData: keepPreviousData,
    gcTime: 0,
    staleTime: 0,
  });
  useLogViewFilterBottomSheet({ logging: data?.logging });
  const { listHeight, setListHeight } = useListHeight();
  const listRef = useRef<ListRef<FormattedChildGroup> | null>(null);

  const handleClickFilter = ({
    queryName,
    queryValue,
    title,
  }: SetFilterListBottomSheetViaQueryValueProps & { title: string }) => {
    setFilterListBottomSheetViaQueryValue({
      queryName,
      queryValue,
    });
    setSelectedTitle(title);
  };
  const [isResetSlider, setIsResetSlider] = useState(false);

  // 모든 childGroup을 flat한 배열로 만들기
  const flatChildGroups = useMemo(
    () => data?.parentGroups.flatMap(parent => parent.childGroups) ?? [],
    [data?.parentGroups],
  );

  useAsyncEffect(async () => {
    if (!selectedTitle || isFetching) {
      return;
    }
    await delay(100);
    // 1. flatChildGroups의 title 순서에 맞게 listHeight를 모두 정렬
    const sortedListHeight: Record<string, number> = {};

    for (const childGroup of flatChildGroups) {
      const title = childGroup.title;

      // flatChildGroups에 있는 title만 포함
      if (listHeight[title] !== undefined) {
        sortedListHeight[title] = listHeight[title];
      }
    }

    // 2. selectedTitle까지 slice
    let sortedAndFilteredListHeight: Record<string, number> = {};

    if (selectedTitle) {
      const sortedTitles = Object.keys(sortedListHeight);
      const selectedIndex = sortedTitles.findIndex(
        title => title === selectedTitle,
      );

      if (selectedIndex !== -1) {
        const slicedTitles = sortedTitles.slice(0, selectedIndex);
        sortedAndFilteredListHeight = slicedTitles.reduce(
          (acc, title) => {
            acc[title] = sortedListHeight[title];
            return acc;
          },
          {} as Record<string, number>,
        );
        // selectedTitle까지만 포함 (selectedTitle 포함)
      } else {
        sortedAndFilteredListHeight = sortedListHeight;
      }
    } else {
      sortedAndFilteredListHeight = sortedListHeight;
    }

    // 모든 높이를 더하기
    const totalHeight = Object.values(sortedAndFilteredListHeight).reduce(
      (sum, height) => sum + height,
      0,
    );
    const bottomSheetHeight = 80;
    const centerHeight = (height - bottomSheetHeight) / 2 - 40;
    listRef.current?.scrollToOffset({
      offset: totalHeight - centerHeight,
      animated: true,
    });
    setSelectedTitle(null);
  }, [listHeight, flatChildGroups, selectedTitle]);

  // focusingParentGroupIndex는 parentGroups 배열의 인덱스이므로
  // flatChildGroups 배열의 인덱스로 변환 필요
  const targetFlatIndex = useMemo(() => {
    if (data?.focusingParentGroupIndex === undefined) return undefined;
    let flatIndex = 0;
    for (let i = 0; i < data.focusingParentGroupIndex; i++) {
      flatIndex += data.parentGroups[i]?.childGroups.length ?? 0;
    }
    return flatIndex;
  }, [data?.focusingParentGroupIndex, data?.parentGroups]);

  useScrollFilterWhenClickChip({
    listRef,
    targetIndex: targetFlatIndex,
    dataLength: flatChildGroups.length,
  });
  if (!data) {
    return null;
  }

  return (
    <>
      {isFetching && isPlaceholderData && (
        <Flex.Center
          flex={1}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1000}
          backgroundColor="white"
          backgroundColorOpacity={0.2}
        >
          <Spinner size={20} />
        </Flex.Center>
      )}
      <List<FormattedChildGroup>
        ref={listRef}
        contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 80 }}
        data={flatChildGroups}
        onScrollToIndexFailed={info => {
          delay(100).then(() => {
            const safeIndex = Math.min(info.index, flatChildGroups.length - 1);
            if (safeIndex >= 0) {
              listRef.current?.scrollToIndex({
                index: safeIndex,
                animated: false,
                viewPosition: 0,
              });
            }
          });
        }}
        renderItem={({ item: childGroup, index }) => {
          switch (childGroup.type) {
            case FilterType.Single:
              return (
                <SingleFilter
                  key={childGroup.title}
                  {...childGroup}
                  handleClickFilter={handleClickFilter}
                  index={index}
                  setListHeight={setListHeight}
                />
              );
            case FilterType.MultiImage:
            case FilterType.MultiFlow:
              // eslint-disable-next-line no-case-declarations
              const Component =
                childGroup.type === FilterType.MultiImage
                  ? MultiImageFilter
                  : MultiFlowFilter;
              return (
                <Component
                  key={childGroup.title}
                  {...childGroup}
                  handleClickFilter={handleClickFilter}
                  index={index}
                  listRef={listRef}
                  setListHeight={setListHeight}
                  setSelectedTitle={setSelectedTitle}
                />
              );
            case FilterType.SingleTree:
              return (
                <SingleTreeFilter
                  key={childGroup.title}
                  {...childGroup}
                  handleClickFilter={handleClickFilter}
                  setListHeight={setListHeight}
                  index={index}
                />
              );
            case FilterType.Slide:
              return (
                <SlideFilter
                  key={childGroup.title}
                  {...childGroup}
                  handleClickFilter={handleClickFilter}
                  isResetSlider={isResetSlider}
                  setIsResetSlider={setIsResetSlider}
                  setListHeight={setListHeight}
                  index={index}
                />
              );
            default:
              return null;
          }
        }}
      />
      <Footer
        closeBottomSheet={closeBottomSheet}
        totalCount={data.totalCount}
        isLoading={isFetching}
        logging={data.logging}
        scrollToFilterList={scrollToFilterList}
        radioQueryName={radioQueryName}
        onResetSlider={() => {
          listRef.current?.scrollToIndex({
            index: 0,
            animated: true,
            viewPosition: 0,
          });
          setIsResetSlider(true);
        }}
      />
    </>
  );
};
